import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ContentTemplate, Divider, Grid, SectionHeader, Typography } from 'web-library';
import Declaration from '../../../../components/Declaration';
import DocumentBadge from '../../../../components/DocumentBadge';
import PaymentButton, { PaymentMethod } from '../../../payment/components/PaymentButton';
import ExamDraftMessage from '../ExamDraftMessage';
import ContactDetails from '../particulars/ContactDetails';
import NextOfKinContactDetails from '../particulars/NextOfKinContactDetails';
import ParentParticulars from '../particulars/ParentParticulars';
import PersonalDetails from '../particulars/PersonalDetails';
import CAECertificateDetails from './CAECertificateDetails';
import CEQScore from './CEQScore';
import ExamDetails from './ExamDetails';
import ExamFee from './ExamFee';
import PersonDeclaring from './PersonDeclaring';
import SchoolDetails from './SchoolDetails';
import SiblingDetails from './SiblingDetails';
import CandidateDetails from './spers/CandidateDetails';
import { useGetPaymentAccess } from '../../../../api-refactored/financial/useGetPaymentAccess';

interface IConfirmAndPayList {
  data: any;
  isAEIS: boolean;
  onPaymentButton: (_callback: any) => void;
  onPaymentOption: (_callback: any, _method: any) => void;
  onSuccess: () => void;
  classes: any;
  errMsg: boolean;
  isDeclared: boolean;
  onAgreement: () => void;
  onBack: () => void;
  isSecondDeclared?: boolean;
  onSecondAgreement?: () => void;
  isThirdDeclared?: boolean;
  onThirdAgreement?: () => void;
  isFourthDeclared?: boolean;
  onFourthAgreement?: () => void;
  isFifthDeclared?: boolean;
  onFifthAgreement?: () => void;
  isPaymentDisabled: boolean;
  onPaymentDisabled: (_isRetry: boolean) => void;
  vacancyExpiredModal: React.ReactNode;
}

const ConfirmAndPayList = ({
  data,
  isAEIS,
  onPaymentButton,
  onPaymentOption,
  onSuccess,
  classes,
  errMsg,
  isDeclared,
  onAgreement,
  isSecondDeclared=false,
  onSecondAgreement = () => {},
  isThirdDeclared=false,
  onThirdAgreement = () => {},
  isFourthDeclared=false,
  onFourthAgreement = () => {},
  isFifthDeclared=false,
  onFifthAgreement = () => {},
  onBack,
  isPaymentDisabled,
  onPaymentDisabled,
  vacancyExpiredModal,
}: IConfirmAndPayList) => {
  const { t } = useTranslation();

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod && data.payment.bankTransferOption) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  return (
    <>
      <ContentTemplate isContinuable type="full">
        <SectionHeader title={t('exam->>NonNationalExam.ConfirmAndPay')} />
        <PersonalDetails
          examCandidateDetail={data.candidateInfo}
          sameRowName
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
        />
        <ContactDetails
          contactDetails={data.candidateInfo}
          sameRowPhone
          sameRowAddress
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
        />
        <NextOfKinContactDetails {...data.candidateInfo} />
        {isAEIS && (
          <ParentParticulars
            parentDetail={{
              fatherDetail: data.fatherDetail,
              motherDetail: data.motherDetail,
            }}
          />
        )}
        <PersonDeclaring
          personDetails={{
            personDeclaring: data.personDeclaring,
            personDeclaringId: data.personDeclaringId,
          }}
        />
        {!isAEIS && (
          <>
            <Divider paddingTop />
            <CandidateDetails
              candidateDetails={{
                certificateNo: data.candidateInfo.birthCertNo,
                dateAttained: data.candidateInfo.sprAttainedDate,
                motherTongueLanguage: data.mtLanguage,
              }}
            />
          </>
        )}
        <Divider paddingTop />
        <ExamDetails
          examDetails={{
            examYear: data.examYear,
            admissionLevel: data.admissionLevel,
            examLevel: data.examLevel,
            mtLanguage: data.mtLanguage,
            isAEIS,
          }}
        />
        <Divider paddingTop />
        {isAEIS && (
          <>
            {data.caeCertificationDetail && (
              <CAECertificateDetails certificateDetails={data.caeCertificationDetail} />
            )}
            {data.ceqScoreDetail && <CEQScore score={data.ceqScoreDetail} />}
          </>
        )}
        <SchoolDetails schoolDetails={data.schoolDetails} isAEIS={isAEIS} />
        {isAEIS && (
          <SiblingDetails siblingDetails={data.siblingDetails} examType={data.examLevel} />
        )}
        {data.supportingDocument.length ? (
          <>
            <Divider paddingTop />
            <Typography variant="h3" className={classes.subheader}>
              {t('exam->>NonNationalExam.SupportingDocuments')}
            </Typography>
            {data.supportingDocument.map((item: string) => (
              <DocumentBadge
                key={item}
                className={classes.documentBadgeContainer}
                documentName={item}
              />
            ))}
          </>
        ) : null}
        <Divider paddingTop />
        <ExamFee
          feeDetails={{
            regNumber: data.gstRegistrationNumber,
            totalExamFee: data.payment.totalExamFee,
            totalGst: data.payment.totalGst,
            totalFee: data.payment.totalFee,
          }}
        />
        <Divider paddingTop />
        {isAEIS ? (
        <>
          <Declaration
            isAgreed={isDeclared}
            isContentJustify
            handleAgreement={onAgreement}
            customContent={
              <span style={{ whiteSpace: 'pre-line' }}>
                {t('exam->>NonNationalExam.aeisDeclarationTextFirst', {
                  examType: data.examLevel,
                })}
              </span>
            }
          />
          <Declaration
            isAgreed={isSecondDeclared}
            isContentJustify
            handleAgreement={onSecondAgreement}
            customContent={t('exam->>NonNationalExam.aeisDeclarationTextSecond', {
                examType: data.examLevel,
            })}
            noTitle={true}
           />
           <Declaration
            isAgreed={isThirdDeclared}
            isContentJustify
            handleAgreement={onThirdAgreement}
            customContent={
                <div>
                  <Typography variant="body1">
                    {t('exam->>NonNationalExam.aeisDeclarationTextThird')}
                  </Typography>
                  <ol type="a">
                    <li>
                      <Typography variant="body1">
                        {t('exam->>NonNationalExam.aeisDeclarationTextThirdPoint1')}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body1">
                         {t('exam->>NonNationalExam.aeisDeclarationTextThirdPoint2')}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body1">
                        {t('exam->>NonNationalExam.aeisDeclarationTextThirdPoint3')}
                      </Typography>
                    </li>
                  </ol>
                  <Typography variant="body1" align="left">
                    {t('exam->>NonNationalExam.aeisDeclarationTextThirdConclusion')}
                  </Typography>
                  <Typography variant="body1" align="left">
                    {t('exam->>NonNationalExam.aeisDeclarationTextThirdNote1')}
                  </Typography>
                  <Typography variant="body1" align="left">
                    {t('exam->>NonNationalExam.aeisDeclarationTextThirdNote2')}
                  </Typography>
                </div>
        }
            noTitle={true}
            />
        <Declaration
          isAgreed={isFourthDeclared}
          isContentJustify
          handleAgreement={onFourthAgreement}
          customContent={t('exam->>NonNationalExam.aeisDeclarationTextFourth', {
              examType: data.examLevel,
          })}
             noTitle={true}
          />
        <Declaration
          isAgreed={isFifthDeclared}
          isContentJustify
          handleAgreement={onFifthAgreement}
          customContent={t('exam->>NonNationalExam.aeisDeclarationTextFifth', {
              examType: data.examLevel,
          })}
              noTitle={true}
          />
        </>
        ) : (
          <Declaration
            isAgreed={isDeclared}
            isContentJustify
            handleAgreement={onAgreement}
            customContent={t('exam->>NonNationalExam.speDeclarationText', {
              examType: data.examLevel,
            })}
            customDeclare={t('exam->>NonNationalExam.speDeclarationTextSecondLine')}
          />
        )}
        <Typography align="justify" className={classes.feePreviewText} variant="body1">
          {t('exam->>NonNationalExam.FeePreviewTextForAEISSPERS')}
        </Typography>
        {errMsg && (
          <Grid xs={12} md={8}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {t('exam->>NonNationalExam.ConfirmError')}
            </Typography>
          </Grid>
        )}
      </ContentTemplate>

      <ContentTemplate continuous>
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          spacing={3}
          className={classes.bottomButtonGroup}
        >
          <Grid xs={12} md={3}>
            <Button
              id="button-selectSubject-back"
              fullWidth
              className={classes.button}
              variant="outlined"
              onClick={() => {
                onBack();
              }}
            >
              {t('exam->>ReviewExam.Back')}
            </Button>
          </Grid>
          <Grid xs={12} md={4}>
            <PaymentButton
              buttonClassName={classes.paymentButton}
              isUniqueLink={false}
              disabled={isPaymentDisabled}
              onPaymentComplete={(isRetry) => {
                onPaymentDisabled(isRetry);
              }}
              paymentMethodsAvailable={paymentAccessDetailsOption}
              onClickPaymentButton={(callback) => {
                onPaymentButton(callback);
              }}
              onHandlePaymentMethod={(callback, method) => {
                onPaymentOption(callback, method);
              }}
              onSuccessPayment={onSuccess}
              fullWidth
              buttonText={t('exam->>ReviewExam.ConfirmAndPay')}
              buttonVariant="contained"
              vacancyExpiredModal={vacancyExpiredModal}
            />
          </Grid>
        </Grid>
        <ExamDraftMessage enableApi={false} />
      </ContentTemplate>
    </>
  );
};

export default ConfirmAndPayList;
